// components/.../written/WrittenTestimonies.tsx
"use client";

import React, { useEffect, useMemo, useState, useRef } from "react";
import Link from "next/link";
import { Formik, Form } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../../../../redux/storeTypes";
import { Plus, MoveRight, SquarePen, Edit3, Trash2 } from "lucide-react";

import {
  fetchSummary,
  fetchDetailBySlug,
  createTestimony,
  updateTestimonyBySlug,
  deleteTestimonyBySlug,
  resetOpStatus,
} from "../../../../../../redux/ugc/testimoniesSlice";
import {
  selectSummary,
  selectSummaryStatus,
  selectDetailBySlug,
  selectDetailStatusBySlug,
  selectOpStatus,
} from "../../../../../../redux/selectors/ugc/testimoniesSelectors";

import { buildYupObject } from "../../../../../../utils/validators/textValidator";
import type { TestimonyDTO } from "../../../../../../types/ugc/testimonyTypes";
import { useAlertMessage } from "../../../../../../hooks/common/useAlertMessage";
import {
  ActionMenu,
  Modal,
  InlineStatus,
  ArticleCardSkeleton,
  Input,
  Button,
  MiniRTE,
  RichTextViewer
} from "../../../../../../components/common";
import { isRTLLanguage, isRTLText } from "../../../../../../utils/helpers/languageHelpers";
import { UGC_ENDPOINTS } from "../../../../../../services/ugc/endpoint/endpoints";
import { makeReadUrl } from "../../../../../../utils/navigation/contentNavigation";

import "./WrittenTestimonies.css";




interface Props {
  isActive?: boolean;
  lang?: string;
}

const WrittenTestimonies: React.FC<Props> = ({ isActive = true, lang = "en" }) => {
  const dispatch = useDispatch<AppDispatch>();
  const navGuard = useRef({ downY: 0, moved: false });

  const { message, showMessage, clearMessage } = useAlertMessage();

  /* ---------- Global state ---------- */
  const summary = useSelector(selectSummary);
  const summaryStatus = useSelector(selectSummaryStatus);
  const writtenSummary = summary?.written ?? { exists: false };
  const writtenSlug = writtenSummary.exists ? writtenSummary.slug : undefined;

  const writtenDetail = useSelector(
    writtenSlug ? selectDetailBySlug(writtenSlug) : () => undefined
  ) as TestimonyDTO | undefined;

  const detailStatus = useSelector(
    writtenSlug ? selectDetailStatusBySlug(writtenSlug) : () => "idle"
  );

  /* ---------- Derived flags ---------- */
  const baseLoading = summaryStatus === "loading";
  const detailLoading = !!writtenSlug && detailStatus === "loading";
  const isLoading = baseLoading || detailLoading;
  const isEmpty = !isLoading && writtenSummary.exists === false;

  /* ---------- Op statuses ---------- */
  const createKey = "create:written";
  const updatingKey = `update:written:${writtenSlug ?? "x"}`;
  const deletingKey = `delete:written:${writtenSlug ?? "x"}`;
  const createStatus = useSelector(selectOpStatus(createKey));
  const updateStatus = useSelector(selectOpStatus(updatingKey));
  const deleteStatus = useSelector(selectOpStatus(deletingKey));

  /* ---------- Effects ---------- */
  useEffect(() => {
    if (summaryStatus === "idle" || summaryStatus === "failed") {
      dispatch(fetchSummary());
    }
  }, [dispatch, summaryStatus]);

  useEffect(() => {
    if (writtenSlug && !writtenDetail && detailStatus !== "loading") {
      dispatch(fetchDetailBySlug(writtenSlug));
    }
  }, [dispatch, writtenSlug, writtenDetail, detailStatus]);

  /* ---------- Navigation ---------- */
  const TESTIMONY_ENDPOINT = UGC_ENDPOINTS.testimonies.root;

  const readUrl = useMemo(() => {
    return writtenDetail?.slug
      ? makeReadUrl(writtenDetail.slug, {
        endpoint: TESTIMONY_ENDPOINT,
        scope: "flat", // member-authenticated context
        title: writtenDetail.title || undefined,
      })
      : null;
  }, [writtenDetail]);


  // Editor modal (Formik) ----------------------------------------------
  type EditorMode = "create" | "edit";
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState<EditorMode>("create");

  const openCreate = () => {
    setEditorMode("create");
    setEditorOpen(true);
  };

  const openEdit = () => {
    setEditorMode("edit");
    setEditorOpen(true);
    // ensure detail is loaded for prefill (no UX change; just safety)
    if (!writtenDetail && writtenSlug) {
      dispatch(fetchDetailBySlug(writtenSlug));
    }
  };

  // Dynamic direction base (by language) — default LTR
  const baseDir: "rtl" | "ltr" = lang ? (isRTLLanguage(lang) ? "rtl" : "ltr") : "ltr";

  // Validation schema ------------------------------------------------
  const Schema = buildYupObject({
    title: {
      label: "Title",
      required: true,
      maxLen: 30,
      trim: true,
      collapseWhitespace: true,
    },
    content: {
      label: "Your testimony",
      required: true,
      minLen: 1,
      maxLen: 8000,
      trim: true,
      collapseWhitespace: true,
    },
  });

  // Submit handler (create/edit) ----------------------------------------
  const submitEditor = async (values: { title?: string; content: string }) => {
    const fd = new FormData();
    fd.append("title", values.title!.trim());
    fd.append("content", values.content.trim());
    if (editorMode === "create") fd.append("type", "written");

    if (editorMode === "create") {
      try {
        const res = await dispatch(
          createTestimony({ type: "written", payload: fd })
        ).unwrap();
        showMessage("Written testimony created.", "success");
        setEditorOpen(false);
        // if (res?.slug) dispatch(fetchDetailBySlug(res.slug));
        dispatch(resetOpStatus(createKey));
      } catch (e: any) {
        const status = e?.response?.status;
        const existingSlug = e?.response?.data?.existing_slug;
        if (status === 409 && existingSlug) {
          const ok = window.confirm(
            "You already have a written testimony. Replace the existing one?"
          );
          if (!ok) return showMessage("Replace canceled.", "info");
          const fd2 = fd;
          try {
            const rep = await dispatch(
              updateTestimonyBySlug({
                type: "written",
                slug: existingSlug,
                payload: fd2,
              })
            ).unwrap();
            showMessage("Written testimony replaced.", "success");
            setEditorOpen(false);
            // if (rep?.slug) dispatch(fetchDetailBySlug(rep.slug));
            dispatch(
              resetOpStatus(updatingKey.replace(":x", `:${existingSlug}`))
            );
          } catch (uErr: any) {
            showMessage(
              uErr?.message || "Failed to replace written testimony.",
              "error"
            );
          }
        } else {
          showMessage(e?.message || "Failed to create written testimony.", "error");
        }
      }
    } else {
      if (!writtenSlug) return;
      try {
        const r = await dispatch(
          updateTestimonyBySlug({ type: "written", slug: writtenSlug, payload: fd })
        ).unwrap();
        showMessage("Written testimony updated.", "success");
        setEditorOpen(false);
        // if (r?.slug) dispatch(fetchDetailBySlug(r.slug));
        dispatch(resetOpStatus(updatingKey));
      } catch (e: any) {
        showMessage(e?.message || "Failed to update.", "error");
      }
    }
  };

  /* ---------- Delete ---------- */
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteAck, setDeleteAck] = useState(false);
  const openDeleteModal = () => {
    setDeleteAck(false);
    setShowDeleteModal(true);
  };
  const handleDeleteConfirm = async () => {
    if (!writtenSlug) return;
    try {
      await dispatch(
        deleteTestimonyBySlug({ type: "written", slug: writtenSlug })
      ).unwrap();
      showMessage("Written testimony deleted.", "success");
      setShowDeleteModal(false);
      dispatch(fetchSummary());
      dispatch(resetOpStatus(deletingKey));
    } catch (e: any) {
      showMessage(e?.message || "Failed to delete.", "error");
    }
  };

  /* ---------- Renders ---------- */
  // Empty state → open editor
  const renderEmpty = () => (
    <div className="written-empty" onClick={openCreate}>
      <div className="written-empty-action">
        <Plus className="plus-btn" size={32} strokeWidth={3} />
        <MoveRight className="move-icone" size={32} />
        <SquarePen className="written-icon" size={36} strokeWidth={1} />
      </div>
      <p className="empty-hint">
        Share your written testimony and let your words encourage others.
      </p>
    </div>
  );

  // Read-only card (click-through to reader) — wrapped by Link
  const renderReady = () => {
    if (!writtenDetail) return null;

    const titleText =
      writtenDetail.title && writtenDetail.title.trim().length
        ? writtenDetail.title
        : "Written testimony";
    const dirTitle: "rtl" | "ltr" = isRTLText(titleText) ? "rtl" : "ltr";

    const contentText = writtenDetail.content || "";
    const dirContent: "rtl" | "ltr" = isRTLText(contentText) ? "rtl" : "ltr";

    const replaceDisabled = updateStatus === "loading";
    const deleteDisabled = deleteStatus === "loading";

    const actionOptions = [
      {
        label: replaceDisabled ? "Updating…" : "Edit",
        icon: <Edit3 size={16} />,
        disabled: replaceDisabled,
        onClick: () => openEdit(),
      },
      {
        label: deleteDisabled ? "Deleting…" : "Delete",
        icon: <Trash2 size={16} />,
        disabled: deleteDisabled,
        danger: true,
        onClick: () => openDeleteModal(),
      },
    ];

    return (
      <div className="t-written-card t-written-card--minimal">
        <div className="written-card-actions-top-right">
          <ActionMenu options={actionOptions} iconSize={32} position="bottom-right" color="primary" />
        </div>
        <Link
          href={readUrl || "#"}
          prefetch
          className="t-written-card-link"
          aria-label="Open written testimony"
          onMouseDown={(e) => { navGuard.current.downY = e.clientY; navGuard.current.moved = false; }}
          onMouseMove={(e) => {
            if (Math.abs(e.clientY - navGuard.current.downY) > 4) navGuard.current.moved = true;
          }}
          onClick={(e) => {
            if (navGuard.current.moved) { e.preventDefault(); e.stopPropagation(); }
          }}
        >
          <div className="t-written-card-body tl-indicator-aurora" data-dir={baseDir} style={{ position: "relative" }}>
            {(detailStatus === "loading") && (
              <ArticleCardSkeleton
                variant="card"
                showImage={false}
                lines={3}
                standalone={false}  // overlay
                className="t-written-skel-overlay"
              />
            )}

            <h3
              className={`written-title ${dirTitle === "rtl" ? "rtl" : "ltr"}`}
              dir={dirTitle}
              title={titleText}
            >
              {titleText}
            </h3>

            <div
              className={`written-excerpt ${dirContent === "rtl" ? "rtl" : "ltr"}`}
              dir={dirContent}
            >
              <RichTextViewer
                value={contentText}
                className="written-excerpt-viewer"
                appearance="dark"
              />
            </div>
          </div>
        </Link>
      </div>
    );
  };

  return (
    <div className="t-written-wrap">

      {/* Skeleton */}
      {isLoading && (
        <ArticleCardSkeleton
          variant="card"
          showImage={false}
          lines={3}
          standalone
          className="t-written-skel"
        />
      )}

      {/* Main states */}
      {!isLoading && (
        <>
          {isEmpty && renderEmpty()}
          {!isEmpty && renderReady()}
        </>
      )}

      {/* Editor Modal (Formik-powered) */}
      <Modal
        isOpen={editorOpen}
        onClose={() => setEditorOpen(false)}
        title={editorMode === "create" ? "Write your testimony" : "Edit your testimony"}
        confirmText={editorMode === "create" ? "Publish" : "Save"}
        confirmVariant="primary"
        hideFooter
      >
        <Formik
          // force remount when switching mode or when current slug changes
          key={`${editorMode}-${writtenDetail?.slug ?? "new"}`} // ensures fresh initialValues
          initialValues={{
            title: editorMode === "edit" ? (writtenDetail?.title || "") : "",
            content: editorMode === "edit" ? (writtenDetail?.content || "") : "",
          }}
          enableReinitialize
          validationSchema={Schema}
          onSubmit={async (vals, helpers) => {
            await submitEditor(vals);
            helpers.setSubmitting(false);
          }}
        >
          {({ values, isSubmitting, isValid }) => {
            const titleDir: "rtl" | "ltr" =
              values.title?.trim().length
                ? isRTLText(values.title)
                  ? "rtl"
                  : "ltr"
                : baseDir;

            return (
              <Form className="written-editor" data-dir={baseDir}>
                {/* Title input (Formik-aware Input) */}
                <Input
                  label="Title"
                  name="title"
                  maxLength={30}
                  placeholder="Max 30 characters"
                  dir={titleDir}
                  required
                />

                {/* Content editor (MiniRTE) */}
                <MiniRTE
                  name="content"
                  label="Your testimony"
                  maxLength={8000}
                  placeholder="Write here…"
                  visualMode="contenteditable"
                // langCode={lang}
                />

                {/* Footer actions inside modal body */}
                <div className="editor-actions">
                  <Button
                    variant="outline"
                    size="sm"
                    type="button"
                    onClick={() => setEditorOpen(false)}
                  >
                    Cancel
                  </Button>

                  <Button
                    variant="primary"
                    size="sm"
                    type="submit"
                    isLoading={isSubmitting}
                    disabled={
                      !isValid ||
                      isSubmitting ||
                      !values.title?.trim() ||
                      !values.content?.trim()
                    }
                  >
                    {editorMode === "create" ? "Publish" : "Save"}
                  </Button>
                </div>
              </Form>
            );
          }}
        </Formik>
      </Modal>

      {/* Delete Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          if (deleteStatus !== "loading") setShowDeleteModal(false);
        }}
        title="Delete written testimony?"
        message={
          <div>
            <p>
              This will permanently delete your written testimony from TownLIT.
              You can write a new one later, but this cannot be recovered.
            </p>
            <label
              className="confirm-row"
              style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12 }}
            >
              <input
                type="checkbox"
                checked={deleteAck}
                onChange={(e) => setDeleteAck(e.target.checked)}
              />
              <span>I understand this action cannot be undone.</span>
            </label>
          </div>
        }
        confirmText={deleteStatus === "loading" ? "Deleting…" : "Delete"}
        cancelText="Cancel"
        onConfirm={handleDeleteConfirm}
        disableBackdropClick={deleteStatus === "loading"}
        isLoading={deleteStatus === "loading"}
        confirmVariant="danger"
        confirmDisabled={!deleteAck}
      />

      {/* Alerts */}
      {message && (
        <InlineStatus
          kind={
            message.type === "error"
              ? "error"
              : message.type === "warning"
                ? "warning"
                : message.type === "success"
                  ? "success"
                  : "info"
          }
          autoHideMs={1600}
          onHide={clearMessage}
          position="top-left"
          title={message.text || undefined}
          size={26}
        />
      )}
    </div>
  );
};

export default WrittenTestimonies;
